// src/screens/ClicksScreen.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getProgress, openWhatsApp, logoutUser, getContacts, saveContact, deleteContact,
  getUserCanjes, createCanje, resetClicksCycle,
  REWARD_LEVELS, getNivelRecompensas, reclamarRecompensaRecarga, reclamarVale, getUserVales,
  notifyAdmin, registrarNivelAlcanzado,
} from '../services/authService';
import { toast } from '../components/UI';
import TransaccionesScreen from './TransaccionesScreen';

// Deriva de REWARD_LEVELS (authService) -- fuente única de verdad del punto 4b.
const LEVELS = [500, 1000, 1500, 2000].map(clicks => ({ clicks, ...REWARD_LEVELS[clicks] }));

const VALE_TIPO_LABEL = { traduccion: 'Traducción', vuelo: 'Vuelo' };
const VALE_ESTADO_STYLE = {
  disponible: { bg: 'rgba(0,196,140,0.15)', color: 'var(--success)', label: '✅ Disponible' },
  usado:      { bg: 'rgba(107,130,168,0.15)', color: 'var(--muted)',  label: '☑️ Usado' },
  vencido:    { bg: 'rgba(255,60,60,0.12)',  color: 'var(--error)',  label: '⌛ Vencido' },
};

const SCAN_CARD_URL = '/.netlify/functions/scan-card';
const MLC_PREFIXES = ['9225', '9235', '9245'];
const UNCLEAR_PHOTO_NOTICE = 'No pudimos leer el número con claridad, por favor verifícalo o escríbelo manualmente.';

// Si el número empieza con un prefijo de MLC, avisamos (no bloquea el guardado).
function getMlcWarning(value) {
  const digits = (value || '').replace(/\D/g, '');
  if (!digits) return null;
  return MLC_PREFIXES.some(p => digits.startsWith(p))
    ? 'Esta tarjeta parece ser de MLC, no de CUP. Verifica que sea la correcta antes de continuar.'
    : null;
}

// Reduce la foto (que puede pesar varios MB en un celular) antes de enviarla a la API.
function resizeImageFile(file, maxDim = 1400, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('No se pudo leer la imagen.'));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) { height = Math.round(height * maxDim / width); width = maxDim; }
          else { width = Math.round(width * maxDim / height); height = maxDim; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve({ base64: dataUrl.split(',')[1], mediaType: 'image/jpeg' });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

async function scanCardPhoto(base64, mediaType) {
  const res = await fetch(SCAN_CARD_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image: base64, mediaType }),
  });
  const data = await res.json().catch(() => ({}));
  return { cardNumber: data.cardNumber || null, confident: !!data.confident };
}

// Campo reutilizable para una tarjeta cubana (sirve tanto al crear un contacto
// como al editar uno existente). Si el campo empieza vacío ofrece tomar foto,
// subir foto o escribir manualmente; si ya tenía un valor guardado, se edita
// como un campo de texto normal (sin opción de foto).
function CardSlotField({ index, value, onChange, canCapture }) {
  const [mode, setMode] = useState(canCapture && !value ? 'picker' : 'input');
  const [scanning, setScanning] = useState(false);
  const [notice, setNotice] = useState(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const mlcWarning = getMlcWarning(value);

  const handleFile = async (file) => {
    if (!file) return;
    setScanning(true);
    setNotice(null);
    try {
      const { base64, mediaType } = await resizeImageFile(file);
      const result = await scanCardPhoto(base64, mediaType);
      if (result.confident && result.cardNumber) {
        onChange(result.cardNumber);
      } else {
        onChange('');
        setNotice(UNCLEAR_PHOTO_NOTICE);
      }
    } catch {
      onChange('');
      setNotice(UNCLEAR_PHOTO_NOTICE);
    }
    setScanning(false);
    setMode('input');
  };

  if (mode === 'picker') {
    return (
      <div style={{ marginBottom: 8 }}>
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; handleFile(f); }} />
        <input ref={galleryInputRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; handleFile(f); }} />
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" disabled={scanning} onClick={() => cameraInputRef.current?.click()} style={{
            flex: 1, background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
            color: 'var(--off-white)', borderRadius: 8, padding: '10px 4px', fontSize: 11, fontWeight: 600,
            opacity: scanning ? 0.6 : 1,
          }}>📷 Tomar foto</button>
          <button type="button" disabled={scanning} onClick={() => galleryInputRef.current?.click()} style={{
            flex: 1, background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
            color: 'var(--off-white)', borderRadius: 8, padding: '10px 4px', fontSize: 11, fontWeight: 600,
            opacity: scanning ? 0.6 : 1,
          }}>🖼️ Subir foto</button>
          <button type="button" disabled={scanning} onClick={() => setMode('input')} style={{
            flex: 1, background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
            color: 'var(--off-white)', borderRadius: 8, padding: '10px 4px', fontSize: 11, fontWeight: 600,
            opacity: scanning ? 0.6 : 1,
          }}>✍️ Escribir</button>
        </div>
        {scanning && <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>⏳ Leyendo tarjeta...</p>}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 8 }}>
      <input value={value} onChange={e => { setNotice(null); onChange(e.target.value); }} placeholder={`Tarjeta ${index}`} inputMode="numeric" />
      {canCapture && !value && (
        <button type="button" onClick={() => { setNotice(null); setMode('picker'); }} style={{
          background: 'none', color: 'var(--muted)', fontSize: 11, marginTop: 4, textDecoration: 'underline',
        }}>← Elegir otro método</button>
      )}
      {notice && <p style={{ fontSize: 11, color: 'var(--gold)', marginTop: 4, lineHeight: 1.4 }}>⚠️ {notice}</p>}
      {mlcWarning && <p style={{ fontSize: 11, color: 'var(--gold)', marginTop: 4, lineHeight: 1.4 }}>⚠️ {mlcWarning}</p>}
    </div>
  );
}

function ContactForm({ uid, existing, onSave, onClose }) {
  const empty = { name: '', phone1: '', phone2: '', phone3: '', card1: '', card2: '', card3: '' };
  const [form, setForm] = useState(existing ? {
    name: existing.name,
    phone1: existing.phone1 || '', phone2: existing.phone2 || '', phone3: existing.phone3 || '',
    card1: existing.card1 || '', card2: existing.card2 || '', card3: existing.card3 || '',
  } : empty);
  const [loading, setLoading] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setCard = k => v => setForm(f => ({ ...f, [k]: v }));
  // Solo se ofrece la opción de foto en las tarjetas que empezaron vacías —
  // una tarjeta ya guardada siempre se edita manualmente, como hoy.
  const canCapture = useRef([!(existing?.card1), !(existing?.card2), !(existing?.card3)]).current;

  const handleSave = async () => {
    if (!form.name.trim()) return toast('El nombre es obligatorio.', 'error');
    setLoading(true);
    try {
      await saveContact(uid, existing ? { ...form, id: existing.id } : form);
      toast('Contacto guardado ✅');
      onSave();
    } catch { toast('Error al guardar.', 'error'); }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300, display: 'flex', alignItems: 'flex-end', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: 'var(--navy-light)', borderRadius: '20px 20px 0 0', width: '100%', padding: '24px 20px 36px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 800 }}>{existing ? 'Editar contacto' : 'Nuevo contacto'}</h3>
          <button onClick={onClose} style={{ background: 'none', color: 'var(--muted)', fontSize: 22 }}>✕</button>
        </div>
        <label>NOMBRE *</label>
        <input value={form.name} onChange={set('name')} placeholder="Ej: Mamá, Pedro..." style={{ marginBottom: 16 }} />
        <p className="section-label">TELÉFONOS (opcional)</p>
        <input value={form.phone1} onChange={set('phone1')} placeholder="Teléfono 1" inputMode="tel" style={{ marginBottom: 8 }} />
        <input value={form.phone2} onChange={set('phone2')} placeholder="Teléfono 2" inputMode="tel" style={{ marginBottom: 8 }} />
        <input value={form.phone3} onChange={set('phone3')} placeholder="Teléfono 3" inputMode="tel" style={{ marginBottom: 16 }} />
        <p className="section-label">TARJETAS CUBANAS (opcional)</p>
        <CardSlotField index={1} value={form.card1} onChange={setCard('card1')} canCapture={canCapture[0]} />
        <CardSlotField index={2} value={form.card2} onChange={setCard('card2')} canCapture={canCapture[1]} />
        <CardSlotField index={3} value={form.card3} onChange={setCard('card3')} canCapture={canCapture[2]} />
        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <button onClick={onClose} className="btn-outline" style={{ flex: 1 }}>Cancelar</button>
          <button onClick={handleSave} className="btn-primary" style={{ flex: 2 }} disabled={loading}>
            {loading ? <span className="spinner" /> : '💾 Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ContactsSection({ uid }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    getContacts(uid).then(c => { setContacts(c); setLoading(false); });
  };

  useEffect(() => { load(); }, [uid]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar el contacto "${name}"?`)) return;
    try {
      await deleteContact(id);
      setContacts(c => c.filter(x => x.id !== id));
      toast('Contacto eliminado.');
    } catch { toast('Error al eliminar.', 'error'); }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <p className="section-label" style={{ margin: 0 }}>MIS CONTACTOS</p>
        {contacts.length < 10 && (
          <button onClick={() => { setEditing(null); setShowForm(true); }} style={{
            background: 'rgba(255,107,0,0.15)', border: '1px solid rgba(255,107,0,0.3)',
            color: 'var(--orange)', borderRadius: 8, padding: '6px 12px',
            fontSize: 12, fontWeight: 700,
          }}>+ Agregar</button>
        )}
      </div>
      {loading && <p style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center' }}>Cargando...</p>}
      {!loading && contacts.length === 0 && (
        <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
          <p style={{ fontSize: 24, marginBottom: 8 }}>👤</p>
          <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
            Guarda los datos de tus familiares en Cuba para usarlos rápido en Recargas y Remesas.
          </p>
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary" style={{ marginTop: 16 }}>
            + Agregar primer contacto
          </button>
        </div>
      )}
      {contacts.map(c => (
        <div key={c.id} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 16, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <p style={{ fontSize: 15, fontWeight: 800, color: 'var(--white)' }}>👤 {c.name}</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { setEditing(c); setShowForm(true); }} style={{
                background: 'rgba(255,107,0,0.12)', border: '1px solid rgba(255,107,0,0.25)',
                color: 'var(--orange)', borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 600,
              }}>✏️ Editar</button>
              <button onClick={() => handleDelete(c.id, c.name)} style={{
                background: 'rgba(255,60,60,0.1)', border: '1px solid rgba(255,60,60,0.2)',
                color: 'var(--error)', borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 600,
              }}>🗑️</button>
            </div>
          </div>
          {[c.phone1, c.phone2, c.phone3].filter(Boolean).map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 12 }}>📞</span>
              <span style={{ fontSize: 13, color: 'var(--off-white)' }}>{p}</span>
            </div>
          ))}
          {[c.card1, c.card2, c.card3].filter(Boolean).map((card, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 12 }}>💳</span>
              <span style={{ fontSize: 13, color: 'var(--gold)' }}>{card}</span>
            </div>
          ))}
        </div>
      ))}
      {contacts.length >= 10 && (
        <p style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', marginTop: 8 }}>
          Límite de 10 contactos alcanzado.
        </p>
      )}
      {showForm && (
        <ContactForm
          uid={uid}
          existing={editing}
          onSave={() => { setShowForm(false); setEditing(null); load(); }}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
}

export default function ClicksScreen() {
  const { user, fireUser, refreshUser } = useAuth();
  const [copied, setCopied] = useState(false);
  const [canjes, setCanjes] = useState([]);
  const [nivelRecompensas, setNivelRecompensas] = useState([]);
  const [vales, setVales] = useState([]);
  const [claiming, setClaiming] = useState(null);
  const [claimingVale, setClaimingVale] = useState(null);
  const [showTransacciones, setShowTransacciones] = useState(false);
  const [expandedLevel, setExpandedLevel] = useState(null);
  const [nivelDataReady, setNivelDataReady] = useState(false);

  const loadCanjes = () => {
    if (user?.uid) getUserCanjes(user.uid).then(setCanjes);
  };
  // Devuelve la promesa: el efecto de abajo encadena .then() para marcar
  // nivelDataReady -- sin el return, abrir la pantalla lanzaba un TypeError
  // y la app entera quedaba en blanco.
  const loadNivelRecompensas = () =>
    user?.uid ? getNivelRecompensas(user.uid).then(setNivelRecompensas) : Promise.resolve();
  const loadVales = () => {
    if (user?.uid) getUserVales(user.uid).then(setVales);
  };

  useEffect(() => { loadCanjes(); loadNivelRecompensas().then(() => setNivelDataReady(true)); loadVales(); }, [user?.uid]);

  // Autocuración: si el usuario ya alcanzó un nivel pero no tiene fila
  // registrada en el ciclo actual (datos de antes de que existiera este
  // sistema, o un depósito que saltó el nivel), se registra en este mismo
  // momento con el plazo completo -- nunca debe quedar un premio real
  // mostrado como "vencido" sin que haya existido una oportunidad
  // verdadera de reclamarlo (antes: por diseño, un nivel saltado nunca
  // generaba fila y quedaba vencido para siempre, sin recurso).
  useEffect(() => {
    if (!nivelDataReady || !user?.uid) return;
    const clicksActuales = user.clicks || 0;
    const resetAt = user.clicks_reset_at;
    const faltantes = LEVELS.filter((lvl) => {
      const reached = clicksActuales >= lvl.clicks;
      const redeemed = canjes.some((c) => c.level === lvl.clicks && (!resetAt || new Date(c.created_at) >= new Date(resetAt)));
      const tieneFila = nivelRecompensas.some((r) => r.nivel === lvl.clicks && (!resetAt || new Date(r.alcanzado_at) >= new Date(resetAt)));
      return reached && !tieneFila && !redeemed;
    });
    if (faltantes.length === 0) return;
    Promise.all(faltantes.map((lvl) => registrarNivelAlcanzado(user.uid, lvl.clicks).catch(() => null)))
      .then(() => getNivelRecompensas(user.uid).then(setNivelRecompensas));
  }, [nivelDataReady, user?.uid, user?.clicks, user?.clicks_reset_at, canjes, nivelRecompensas]);

  if (!user) return null;

  const clicks = user.clicks || 0;
  const progress = getProgress(clicks);
  const avatarLetter = user.name?.[0]?.toUpperCase() || '?';
  const photoUrl = fireUser?.user_metadata?.avatar_url;

  const isRedeemedThisCycle = (lvl) => canjes.find(c =>
    c.level === lvl.clicks &&
    (!user.clicks_reset_at || new Date(c.created_at) >= new Date(user.clicks_reset_at))
  );

  // Fila de nivel_recompensas del ciclo ACTUAL para un nivel dado -- es la
  // única fuente de verdad de si hay oportunidad de canje vigente. Todo nivel
  // alcanzado genera su fila (aunque el depósito haya cruzado varios de
  // una vez, o los CLICKs vinieran de un bono de referido) -- si no hay
  // fila es porque de verdad nunca se llegó a ese nivel en este ciclo.
  const currentCycleRow = (lvl) => nivelRecompensas.find(r =>
    r.nivel === lvl.clicks &&
    (!user.clicks_reset_at || new Date(r.alcanzado_at) >= new Date(user.clicks_reset_at))
  );

  const handleCopy = () => {
    navigator.clipboard.writeText(user.referral_code || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast('¡Código copiado! 📋');
  };

  const handleShareLink = async () => {
    const link = `https://app.clicktravelagency.com.br/?ref=${user.referral_code || ''}`;
    const message = `Únete a Click Travel Agency con mi enlace y ambos ganamos 50 CLICKs:`;
    if (navigator.share) {
      try { await navigator.share({ text: message, url: link }); } catch { /* usuario canceló */ }
    } else {
      navigator.clipboard.writeText(link);
      toast('¡Enlace copiado! 📋');
    }
  };

  const handleClaimLevel = async (lvl) => {
    setClaiming(lvl.clicks);
    try {
      await reclamarRecompensaRecarga(user.uid, lvl.clicks);
      await createCanje({
        uid: user.uid,
        userName: user.name,
        userEmail: user.email,
        level: lvl.clicks,
        rechargePrice: lvl.recharge,
        discountedPrice: lvl.discounted,
      });
      const msg = `🎁 *Solicitud de Canje de CLICKs*\n───────────────────\n👤 Nombre: *${user.name}*\n⭐ Nivel alcanzado: *${lvl.clicks} CLICKs*\n📱 Recarga: *R$${lvl.recharge} con 50% de descuento*\n💰 Precio a pagar: *R$${lvl.discounted}*\n🔢 CLICKs actuales: *${clicks}*\n───────────────────\n¡Quiero canjear mi premio!`;
      openWhatsApp(msg);
      notifyAdmin(`🎁 ${user.name} quiere canjear ${lvl.clicks} CLICKs (recarga R$${lvl.discounted})`, '/?tab=more&admin=canjes');
      if (lvl.clicks === 2000) {
        await resetClicksCycle(user.uid);
        await refreshUser();
        toast('¡Canje enviado! CLICKs reiniciados a 0 — nuevo ciclo iniciado.');
      } else {
        toast('¡Solicitud de canje enviada! 🎉');
      }
      loadCanjes();
      loadNivelRecompensas();
    } catch (err) {
      toast(err?.message || 'Error al procesar el canje.', 'error');
    }
    setClaiming(null);
  };

  const handleReclamarVale = async (lvl, tipo) => {
    const key = `${lvl.clicks}-${tipo}`;
    setClaimingVale(key);
    try {
      const reward = lvl[tipo]; // { descuento, dias }
      const vale = await reclamarVale(user.uid, lvl.clicks, tipo, reward.descuento);
      notifyAdmin(`🎁 ${user.name} quiere canjear vale de ${VALE_TIPO_LABEL[tipo].toLowerCase()} (${lvl.clicks} CLICKs)`, '/?tab=more&admin=canjes');
      toast(`¡Vale generado! Código: ${vale.codigo} 🎉`);
      loadNivelRecompensas();
      loadVales();
    } catch (err) {
      toast(err?.message || 'Error al generar el vale.', 'error');
    }
    setClaimingVale(null);
  };

  return (
    <div className="page" style={{ paddingTop: 24 }}>

      {/* Perfil */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        {photoUrl ? (
          <img src={photoUrl} alt={user.name} style={{ width: 68, height: 68, borderRadius: '50%', margin: '0 auto 12px', border: '3px solid var(--orange)', display: 'block' }} />
        ) : (
          <div style={{ width: 68, height: 68, margin: '0 auto 12px', background: 'linear-gradient(135deg, var(--orange), var(--gold))', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800, color: 'white' }}>
            {avatarLetter}
          </div>
        )}
        <h2 style={{ fontSize: 18, fontWeight: 800 }}>{user.name}</h2>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{user.email}</p>
      </div>

      {/* CLICKs card */}
      <div style={{ background: 'linear-gradient(135deg, #1A1500, #0F2010)', border: '1px solid rgba(255,184,0,0.25)', borderRadius: 20, padding: 20, marginBottom: 16, textAlign: 'center' }}>
        <p style={{ fontSize: 11, color: 'var(--gold)', fontWeight: 700, letterSpacing: 1.5, marginBottom: 4 }}>MIS CLICKS</p>
        <p style={{ fontSize: 56, fontWeight: 800, color: '#FFFFFF', lineHeight: 1 }}>{clicks}</p>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>puntos acumulados</p>
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>Nivel: {progress.current}</span>
            <span style={{ fontSize: 11, color: 'var(--gold)', fontWeight: 700 }}>{Math.round(progress.pct * 100)}% → {progress.next}</span>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
            <div style={{ width: `${Math.round(progress.pct * 100)}%`, height: '100%', background: 'linear-gradient(90deg, var(--orange), var(--gold))', borderRadius: 4, transition: 'width 0.8s ease' }} />
          </div>
        </div>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
          {progress.pct >= 1 ? '¡Nivel máximo de progreso alcanzado!' : `${progress.next - clicks} CLICKs más para tu próximo nivel`}
        </p>
      </div>

      {/* Tabla de recompensas */}
      <p className="section-label">TABLA DE RECOMPENSAS</p>
      <div style={{ marginBottom: 20 }}>
        {LEVELS.map(lvl => {
          const reached = clicks >= lvl.clicks;
          const redeemed = isRedeemedThisCycle(lvl);
          const nivelRow = currentCycleRow(lvl);
          const now = new Date();
          // La recarga ya no vence: basta con haber alcanzado el nivel y no haberla canjeado.
          const canClaim = !!nivelRow && reached && !redeemed;

          const expanded = expandedLevel === lvl.clicks;

          return (
            <div key={lvl.clicks} style={{ background: canClaim ? 'rgba(255,184,0,0.08)' : 'var(--navy-card)', border: `1px solid ${canClaim ? 'rgba(255,184,0,0.4)' : reached ? 'rgba(0,196,140,0.3)' : 'var(--navy-border)'}`, borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
              <div onClick={() => setExpandedLevel(expanded ? null : lvl.clicks)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 16 }}>{reached ? '✅' : '⬜'}</span>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 700, color: canClaim ? 'var(--gold)' : reached ? 'var(--success)' : 'var(--muted)' }}>{lvl.clicks} CLICKs</p>
                    <p style={{ fontSize: 11, color: 'var(--muted)' }}>Recarga R${lvl.recharge} al 50%{lvl.traduccion ? ' + vale traducción' : ''}{lvl.vuelo ? ' + vale vuelo' : ''}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <p style={{ fontSize: 14, fontWeight: 800, color: canClaim ? 'var(--gold)' : 'var(--muted)' }}>R${lvl.discounted}</p>
                  <span style={{ fontSize: 11, color: 'var(--muted)', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
                </div>
              </div>

              {expanded && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--navy-border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: 'var(--off-white)' }}>📱 Recarga de R${lvl.recharge}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--white)' }}>Pagas R${lvl.discounted}</span>
                  </div>
                  {lvl.traduccion && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--off-white)' }}>📄 Vale de traducción</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--gold)' }}>{lvl.traduccion.descuento}</span>
                    </div>
                  )}
                  {lvl.vuelo && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--off-white)' }}>✈️ Vale de vuelo</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--gold)' }}>{lvl.vuelo.descuento}</span>
                    </div>
                  )}
                  {lvl.resetOnClaim && (
                    <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>⚠️ Al canjear la recarga de este nivel, tus CLICKs se reinician a 0.</p>
                  )}
                  <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>
                    La recarga no vence.{lvl.traduccion ? ' Tienes 30 días para reclamar el vale de traducción' : ''}{lvl.vuelo ? ' y 90 días para el vale de vuelo' : ''}{lvl.traduccion ? ' desde que alcances este nivel.' : ''}
                  </p>
                </div>
              )}

              {canClaim && (
                <button onClick={() => handleClaimLevel(lvl)} disabled={claiming === lvl.clicks} style={{
                  width: '100%', marginTop: 10, background: 'linear-gradient(135deg, var(--gold), var(--orange))',
                  color: 'var(--navy)', fontWeight: 800, fontSize: 13, borderRadius: 8, padding: '10px 0',
                  opacity: claiming === lvl.clicks ? 0.6 : 1,
                }}>
                  {claiming === lvl.clicks ? <span className="spinner" /> : `🎁 Canjear recarga — R$${lvl.discounted}`}
                </button>
              )}

              {/* Vales de traducción/vuelo -- solo niveles 1000+/1500+, mientras haya oportunidad vigente */}
              {['traduccion', 'vuelo'].map(tipo => {
                if (!lvl[tipo] || !nivelRow) return null;
                const reclamada = nivelRow[`${tipo}_reclamada`];
                const expiraAt = nivelRow[`${tipo}_expira_at`];
                const vencida = expiraAt && new Date(expiraAt) < now;
                if (reclamada || vencida) return null;
                const key = `${lvl.clicks}-${tipo}`;
                return (
                  <button key={tipo} onClick={() => handleReclamarVale(lvl, tipo)} disabled={claimingVale === key} style={{
                    width: '100%', marginTop: 8, background: 'rgba(255,184,0,0.10)', border: '1px solid rgba(255,184,0,0.3)',
                    color: 'var(--gold)', fontWeight: 700, fontSize: 12.5, borderRadius: 8, padding: '9px 0',
                    opacity: claimingVale === key ? 0.6 : 1,
                  }}>
                    {claimingVale === key ? <span className="spinner" /> : `🎫 Reclamar vale de ${VALE_TIPO_LABEL[tipo].toLowerCase()} — ${lvl[tipo].descuento}`}
                  </button>
                );
              })}

              {redeemed && (
                <span style={{
                  display: 'inline-block', marginTop: 10, fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                  borderRadius: 6, padding: '4px 10px',
                  background: redeemed.status === 'procesado' ? 'rgba(0,196,140,0.15)' : 'rgba(255,107,0,0.15)',
                  color: redeemed.status === 'procesado' ? 'var(--success)' : 'var(--orange)',
                }}>
                  {redeemed.status === 'procesado' ? '✅ Procesado' : '🕓 Pendiente'}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Mis vales */}
      {vales.length > 0 && (
        <>
          <p className="section-label">MIS VALES</p>
          <div style={{ marginBottom: 20 }}>
            {vales.map(v => {
              const st = VALE_ESTADO_STYLE[v.estado] || VALE_ESTADO_STYLE.disponible;
              return (
                <div key={v.id} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: 14, marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <p style={{ fontSize: 15, fontWeight: 800, color: 'var(--white)', letterSpacing: 0.5 }}>{v.codigo}</p>
                    <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 6, padding: '3px 9px', background: st.bg, color: st.color }}>{st.label}</span>
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--off-white)' }}>{VALE_TIPO_LABEL[v.tipo] || v.tipo} · {v.descuento}</p>
                  <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                    {v.estado === 'usado' && v.usado_at
                      ? `Usado el ${new Date(v.usado_at).toLocaleDateString('es', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
                      : `Vence el ${new Date(v.expira_at).toLocaleDateString('es', { day: '2-digit', month: '2-digit', year: 'numeric' })}`}
                  </p>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Cómo acumular */}
      <p className="section-label">CÓMO ACUMULAR</p>
      <div className="card" style={{ marginBottom: 20 }}>
        {[
          { label: 'Remesas',      rule: '1 BRL = 1 CLICK',      icon: '💸' },
          { label: 'Traducciones', rule: '1 BRL = 1 CLICK',      icon: '📄' },
          { label: 'Recargas',     rule: '1 BRL = 2 CLICKs ⭐⭐', icon: '📱' },
        ].map((r, i) => (
          <div key={i}>
            {i > 0 && <div className="divider" />}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 20 }}>{r.icon}</span>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600 }}>{r.label}</p>
                <p style={{ fontSize: 12, color: 'var(--muted)' }}>{r.rule}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Contactos */}
      {user.uid && <ContactsSection uid={user.uid} />}

      {/* Código de referido */}
      <p className="section-label">MI CÓDIGO DE REFERIDO</p>
      <button onClick={handleCopy} style={{ width: '100%', background: 'var(--navy-card)', border: '1px solid rgba(255,107,0,0.3)', borderRadius: 14, padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, cursor: 'pointer' }}>
        <div style={{ textAlign: 'left' }}>
          <p style={{ fontSize: 22, fontWeight: 800, color: 'var(--orange)', letterSpacing: 2 }}>{user.referral_code || 'CLICK-???'}</p>
          <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Tú ganas 50 CLICKs y tu amigo también</p>
        </div>
        <span style={{ fontSize: 20 }}>{copied ? '✅' : '📋'}</span>
      </button>
      <button onClick={handleShareLink} style={{ width: '100%', background: 'rgba(255,107,0,0.1)', border: '1px solid rgba(255,107,0,0.3)', color: 'var(--orange)', fontWeight: 700, fontSize: 13, borderRadius: 12, padding: '12px 0', marginBottom: 8 }}>
        🔗 Compartir enlace
      </button>
      <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.5, paddingLeft: 4 }}>
        ⚠️ Los CLICKs del referido se asignan solo cuando la persona invitada cierre su primer servicio.
      </p>

      {/* Transacciones */}
      <button onClick={() => setShowTransacciones(true)} style={{ width: '100%', background: 'none', color: 'var(--muted)', fontSize: 13, fontWeight: 600, textAlign: 'center', marginBottom: 24, padding: '4px 0', textDecoration: 'underline' }}>
        Ver mis transacciones
      </button>

      {/* Cerrar sesión */}
      <button onClick={async () => { await logoutUser(); }} className="btn-outline" style={{ width: '100%', borderColor: 'var(--navy-border)', color: 'var(--muted)' }}>
        Cerrar sesión
      </button>

      {showTransacciones && <TransaccionesScreen onClose={() => setShowTransacciones(false)} />}
    </div>
  );
}
